import { Box, Typography } from "@mui/material";
import { Link, useLocation } from "react-router";
import { useState, useEffect, useCallback } from "react";
import { getCategoryFromQuery } from "../../utils/getCategoryFromQuery";
import { getAvailablePuppies } from "../../services";

type SidebarSection = {
  title: string;
  links: { label: string; to: string }[];
};

const sections: SidebarSection[] = [
  {
    title: "Dogs",
    links: [
      { label: "Females", to: "/dogs?category=female" },
      { label: "Males", to: "/dogs?category=male" },
    ],
  },
  {
    title: "Puppies",
    links: [{ label: "Available puppies", to: "/puppies" }],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const currentCategory = getCategoryFromQuery(location);

  const [puppies, setPuppies] = useState<any[]>([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAvailablePuppies()
      .then((res) => setPuppies(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % puppies.length);
  }, [puppies.length]);

  useEffect(() => {
    if (puppies.length <= 1) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [puppies.length, next]);

  const puppy = puppies[current];

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: "100%", md: 250 },
        flexShrink: 0,
      }}
    >
      {/* Available Puppies Carousel */}
      <Box
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#b68a58",
            color: "#fff",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#f5e6c8",
            }}
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 1,
              fontSize: "0.8rem",
            }}
          >
            Available Puppies
          </Typography>
        </Box>
        {loading ? (
          <Box
            sx={{
              width: "100%",
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#d4b483",
            }}
          >
            <Typography variant="body2" sx={{ color: "#fff" }}>
              Loading...
            </Typography>
          </Box>
        ) : puppy ? (
          <Link
            to={`/puppy/${puppy.id}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                component="img"
                src={puppy.photo}
                alt={puppy.name}
                sx={{
                  width: "100%",
                  height: 200,
                  objectFit: "cover",
                  display: "block",
                  backgroundColor: "#d4b483",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(transparent, rgba(74,55,40,0.8))",
                  px: 1.5,
                  py: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#f5e6c8",
                    fontFamily: '"Cinzel", serif',
                    fontSize: "0.8rem",
                  }}
                >
                  {puppy.name}
                </Typography>
              </Box>
              {/* Dots indicator */}
              {puppies.length > 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 8,
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  {puppies.map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor:
                          i === current ? "#f5e6c8" : "rgba(245,230,200,0.4)",
                        transition: "background-color 0.3s",
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Link>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#d4b483",
            }}
          >
            <Typography variant="body2" sx={{ color: "#fff" }}>
              No puppies available
            </Typography>
          </Box>
        )}
      </Box>

      {sections.map((section) => (
        <Box
          key={section.title}
          sx={{
            mb: 2,
            backgroundColor: "rgba(255,255,255,0.6)",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#b68a58",
              color: "#fff",
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#f5e6c8",
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 1,
                fontSize: "0.8rem",
              }}
            >
              {section.title}
            </Typography>
          </Box>
          <Box sx={{ py: 0.5 }}>
            {section.links.map((link) => {
              let isActive = false;
              if (section.title === "Dogs") {
                // For dog category links, check ?category
                if (
                  link.label === "Females" &&
                  location.pathname.startsWith("/dogs") &&
                  currentCategory === "female"
                ) {
                  isActive = true;
                } else if (
                  link.label === "Males" &&
                  location.pathname.startsWith("/dogs") &&
                  currentCategory === "male"
                ) {
                  isActive = true;
                }
              } else {
                isActive =
                  location.pathname === link.to ||
                  (link.to !== "/" &&
                    location.pathname.startsWith(link.to.split("?")[0]));
              }
              return (
                <Box
                  key={link.to}
                  component={Link}
                  to={link.to}
                  sx={{
                    display: "block",
                    px: 2,
                    py: 0.8,
                    textDecoration: "none",
                    color: isActive ? "#b68a58" : "#4a3728",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: "0.9rem",
                    borderLeft: isActive
                      ? "3px solid #b68a58"
                      : "3px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "rgba(182,138,88,0.1)",
                      color: "#b68a58",
                    },
                  }}
                >
                  {link.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default Sidebar;
